import {
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	Icon,
	Image,
	Input,
	InputGroup,
	InputLeftAddon,
	Spinner,
	Text,
	useColorModeValue,
	Wrap,
	WrapItem
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { MdContentCopy, MdArrowUpward, MdArrowDownward, MdCheckCircle, MdPix, MdCreditCard, MdOpenInNew } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { fetchBalance, fetchStatement } from '../../store/features/credit/credit.slice';
import { createPix, createCard, quoteCard, pollPayment } from '../../store/features/payment/payment.slice';
import { PaymentMethod, PaymentStatus } from '../../models/payment.model';
import { toastService } from '../../services/toast.service';
import { formatBRL, formatPrice, formatQueries } from '../../utils/format';

const PRESETS = [50, 100, 500, 1000];
const MIN_RECHARGE = 50;

const TX_META: Record<string, { color: string; sign: string; up: boolean }> = {
	PURCHASE: { color: 'green.500', sign: '+', up: true },
	REFUND: { color: 'green.500', sign: '+', up: true },
	ADMIN_ADJUST: { color: 'blue.500', sign: '±', up: true },
	CONSUMPTION: { color: 'red.500', sign: '-', up: false }
};

export default function Creditos() {
	const dispatch = useAppDispatch();
	const { balance, statement, loading } = useAppSelector((state: RootState) => state.credit);
	const { current, loading: payLoading, quote, quoting } = useAppSelector((state: RootState) => state.payment);
	const { pricePerQuery } = useAppSelector((state: RootState) => state.auth);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const tileBg = useColorModeValue('secondaryGray.300', 'navy.900');

	// String para permitir digitar valores decimais (o input type=number come o ponto).
	const [amount, setAmount] = useState<string>('100');
	const [method, setMethod] = useState<PaymentMethod>('PIX');
	const pollRef = useRef<any>(null);

	useEffect(() => {
		dispatch(fetchBalance());
		dispatch(fetchStatement());
	}, [dispatch]);

	// Cotação do cartão (debounce) sempre que o valor/método mudar.
	useEffect(() => {
		if (method !== 'CARD') return;
		const value = Number(amount);
		if (!value || value < MIN_RECHARGE) return;
		const t = setTimeout(() => {
			dispatch(quoteCard(value));
		}, 400);
		return () => clearTimeout(t);
	}, [amount, method, dispatch]);

	useEffect(() => {
		if (current && current.status === PaymentStatus.PENDING && current.paymentId) {
			pollRef.current = setInterval(async () => {
				const res: any = await dispatch(pollPayment(current.paymentId));
				if (res.payload?.status === PaymentStatus.PAID) {
					clearInterval(pollRef.current);
					toastService.success('Pagamento confirmado! Créditos adicionados.');
					dispatch(fetchBalance());
					dispatch(fetchStatement());
				}
			}, 5000);
		}
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, [current, dispatch]);

	const generatePix = () => {
		const value = Number(amount);
		if (!value || value < MIN_RECHARGE) {
			toastService.error(`O valor mínimo para recarga é ${formatBRL(MIN_RECHARGE)}`);
			return;
		}
		dispatch(createPix(value));
	};

	const generateCard = async () => {
		const value = Number(amount);
		if (!value || value < MIN_RECHARGE) {
			toastService.error(`O valor mínimo para recarga é ${formatBRL(MIN_RECHARGE)}`);
			return;
		}
		const res: any = await dispatch(createCard(value));
		if (res.payload?.checkoutUrl) {
			window.open(res.payload.checkoutUrl, '_blank');
		}
	};

	const copyPix = () => {
		if (current?.pixCopiaECola) {
			navigator.clipboard.writeText(current.pixCopiaECola);
			toastService.success('Código PIX copiado');
		}
	};

	const isPaid = current?.status === PaymentStatus.PAID;
	const showQuote = method === 'CARD' && quote && Number(amount) === quote.credits;

	return (
		<Box pt='10px'>
			<Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap='20px'>
				{/* Comprar créditos */}
				<Card>
					<Text fontWeight='800' fontSize='lg' color={textColor} mb='4px'>
						Comprar créditos
					</Text>
					<Text color={subColor} fontSize='sm' mb='16px'>
						Saldo atual: <b>{formatBRL(balance)}</b> · dá para <b>{formatQueries(balance, pricePerQuery)}</b> consultas
						{pricePerQuery > 0 && <> a {formatPrice(pricePerQuery)} cada</>}
					</Text>

					{/* Seletor de método */}
					<Flex gap='10px' mb='16px'>
						<Button
							flex='1'
							variant={method === 'PIX' ? 'brand' : 'lightBrand'}
							leftIcon={<Icon as={MdPix} />}
							onClick={() => setMethod('PIX')}>
							PIX
						</Button>
						<Button
							flex='1'
							variant={method === 'CARD' ? 'brand' : 'lightBrand'}
							leftIcon={<Icon as={MdCreditCard} />}
							onClick={() => setMethod('CARD')}>
							Cartão
						</Button>
					</Flex>

					<Wrap mb='16px'>
						{PRESETS.map(p => (
							<WrapItem key={p}>
								<Button variant={Number(amount) === p ? 'brand' : 'lightBrand'} onClick={() => setAmount(String(p))}>
									{formatBRL(p)}
								</Button>
							</WrapItem>
						))}
					</Wrap>

					<InputGroup mb='6px' size='lg'>
						<InputLeftAddon borderRadius='16px 0 0 16px'>R$</InputLeftAddon>
						<Input
							inputMode='decimal'
							variant='main'
							borderRadius='0 16px 16px 0'
							value={amount}
							onChange={e => {
								const v = e.target.value;
								if (v === '' || /^\d*\.?\d*$/.test(v)) setAmount(v);
							}}
						/>
					</InputGroup>
					<Text fontSize='xs' color={subColor} mb='16px'>
						Valor mínimo: {formatBRL(MIN_RECHARGE)} · você recebe {formatBRL(Number(amount) || 0)} em créditos
						{pricePerQuery > 0 && <> ≈ {formatQueries(Number(amount) || 0, pricePerQuery)} consultas</>}
					</Text>

					{method === 'PIX' ? (
						<Button variant='brand' w='100%' size='lg' onClick={generatePix} isLoading={payLoading}>
							Gerar PIX
						</Button>
					) : (
						<>
							{/* Cotação do cartão: o cliente paga a taxa por cima. */}
							<Box mb='12px' bg={tileBg} borderRadius='16px' p='16px'>
								{quoting && !showQuote ? (
									<Flex align='center' gap='8px' color={subColor}>
										<Spinner size='sm' /> <Text fontSize='sm'>Calculando total...</Text>
									</Flex>
								) : showQuote ? (
									<>
										<Flex justify='space-between'>
											<Text fontSize='sm' color={subColor}>
												Créditos
											</Text>
											<Text fontSize='sm' color={textColor}>
												{formatBRL(quote!.credits)}
											</Text>
										</Flex>
										<Flex justify='space-between'>
											<Text fontSize='sm' color={subColor}>
												Taxa do cartão ({(quote!.cardPct * 100).toFixed(2)}% + {formatBRL(quote!.cardFixedBRL)})
											</Text>
											<Text fontSize='sm' color={textColor}>
												{formatBRL(quote!.feeBRL)}
											</Text>
										</Flex>
										<Divider my='8px' />
										<Flex justify='space-between' align='center'>
											<Text fontWeight='700' color={textColor}>
												Você paga
											</Text>
											<Text fontWeight='800' fontSize='lg' color={textColor}>
												{formatBRL(quote!.amountBRL)}
											</Text>
										</Flex>
										<Text fontSize='xs' color={subColor} mt='4px'>
											Em até {quote!.maxInstallments}x no cartão. O crédito liberado é de {formatBRL(quote!.credits)}
										{pricePerQuery > 0 && <> ≈ {formatQueries(quote!.credits, pricePerQuery)} consultas</>}.
										</Text>
									</>
								) : (
									<Text fontSize='sm' color={subColor}>
										Informe um valor (mínimo {formatBRL(MIN_RECHARGE)}) para ver o total com a taxa do cartão.
									</Text>
								)}
							</Box>
							<Button
								variant='brand'
								w='100%'
								size='lg'
								leftIcon={<Icon as={MdCreditCard} />}
								onClick={generateCard}
								isLoading={payLoading}
								isDisabled={!showQuote}>
								Pagar com cartão
							</Button>
						</>
					)}

					{current && (
						<Box mt='24px' bg={tileBg} borderRadius='16px' p='20px'>
							{isPaid ? (
								<Flex align='center' gap='10px' color='green.500'>
									<Icon as={MdCheckCircle} w='28px' h='28px' />
									<Text fontWeight='700'>Pagamento confirmado!</Text>
								</Flex>
							) : current.method === 'CARD' ? (
								<>
									<Text fontWeight='700' color={textColor} mb='8px'>
										Checkout do cartão gerado
									</Text>
									<Text fontSize='sm' color={subColor} mb='12px'>
										Abrimos o checkout em uma nova aba. Conclua o pagamento por lá — os créditos entram automaticamente ao
										confirmar.
									</Text>
									{current.checkoutUrl && (
										<Button
											as='a'
											href={current.checkoutUrl}
											target='_blank'
											variant='lightBrand'
											size='sm'
											rightIcon={<Icon as={MdOpenInNew} />}>
											Abrir checkout
										</Button>
									)}
									<Text fontSize='xs' color={subColor} mt='10px'>
										Aguardando pagamento... você paga {formatBRL(current.amountBRL)} · recebe {current.credits} créditos
									</Text>
								</>
							) : (
								<>
									<Flex align='center' justify='center' mb='12px'>
										{current.pixQrCode ? (
											<Image
												src={
													current.pixQrCode.startsWith('data:')
														? current.pixQrCode
														: `data:image/png;base64,${current.pixQrCode}`
												}
												alt='QR Code PIX'
												boxSize='180px'
											/>
										) : (
											<Spinner color='brand.500' />
										)}
									</Flex>
									<Text fontSize='xs' color={subColor} mb='6px'>
										PIX copia e cola:
									</Text>
									<Flex gap='8px'>
										<Input value={current.pixCopiaECola || ''} isReadOnly variant='main' size='sm' fontSize='xs' />
										<Button variant='lightBrand' size='sm' onClick={copyPix}>
											<Icon as={MdContentCopy} />
										</Button>
									</Flex>
									<Text fontSize='xs' color={subColor} mt='10px'>
										Aguardando pagamento... {formatBRL(current.amountBRL)} · {current.credits} créditos
									</Text>
								</>
							)}
						</Box>
					)}
				</Card>

				{/* Extrato */}
				<Card>
					<Text fontWeight='800' fontSize='lg' color={textColor} mb='16px'>
						Extrato
					</Text>
					{loading && <Spinner color='brand.500' />}
					{!loading && statement.length === 0 && (
						<Text color={subColor} fontSize='sm'>
							Nenhuma movimentação ainda.
						</Text>
					)}
					<Box maxH='520px' overflowY='auto'>
						{statement.map(tx => {
							const meta = TX_META[tx.type] || TX_META.ADMIN_ADJUST;
							return (
								<Box key={tx.id}>
									<Flex justify='space-between' align='center' py='12px'>
										<Flex align='center' gap='12px'>
											<Flex
												align='center'
												justify='center'
												w='36px'
												h='36px'
												borderRadius='10px'
												bg={tileBg}
												color={meta.color}>
												<Icon as={meta.up ? MdArrowUpward : MdArrowDownward} />
											</Flex>
											<Box>
												<Text fontSize='sm' fontWeight='600' color={textColor}>
													{tx.description || tx.type}
												</Text>
												<Text fontSize='xs' color={subColor}>
													{tx.createdAt ? format(new Date(tx.createdAt), 'dd/MM/yyyy HH:mm') : ''}
												</Text>
											</Box>
										</Flex>
										<Box textAlign='right'>
											<Text fontWeight='700' color={meta.color}>
												{meta.sign}
												{formatBRL(Math.abs(tx.amount))}
											</Text>
											<Text fontSize='xs' color={subColor}>
												Saldo: {formatBRL(tx.balanceAfter)}
											</Text>
										</Box>
									</Flex>
									<Divider />
								</Box>
							);
						})}
					</Box>
				</Card>
			</Grid>
		</Box>
	);
}
